import { Component, computed, effect, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { Producer } from 'depoto-core/src/entities'
import { historyBack } from '../../utils'
import { NotificationService } from '../../services'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { TranslateService } from '@ngx-translate/core'
import { TitleService } from '../../services'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { ProducerService } from '../../services/entity-services/producer.service'
import { updateCachedListQueries } from '../../services/query-cache.service'

export const producerSchema = {
  id: null,
  name: null,
  externalId: null,
  company: {
    id: null,
    name: null,
  },
}

@Component({
  selector: 'app-producer',
  templateUrl: 'producer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProducerComponent implements CheckDataChanges, OnDestroy {
  private producerService = inject(ProducerService)
  private queryClient = injectQueryClient()

  // Overridden in the constructor below and restored in ngOnDestroy - see there for why.
  private previousShouldReuseRoute: Router['routeReuseStrategy']['shouldReuseRoute']

  private initialFormValue = signal<any>(null)
  submitted = signal(false)

  producerForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, [Validators.required]),
  })

  readonly queryProducer = injectQuery(() => ({
    queryKey: ['producer', Number(this.route.snapshot.params['id'])],
    queryFn: async () => {
      const id = this.route.snapshot.params['id']
      if (id) {
        return await this.producerService.getProducer(id, producerSchema)
      } else {
        return new Producer()
      }
    },
    onSuccess: data => {
      // todo check -> if new Producer() is returned, why do i don't want to fillFormValues?
      if (data?.id) this.fillFormValue(data)
    },
  }))

  createProducer = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      if (this.producerForm.invalid) {
        throw new Error('Form is invalid')
      }
      const n = this.getDataToSave()

      return await this.producerService.createProducer(n, producerSchema) // todo add schema
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.producerForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.producerForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.producer.created'))
      // Update detail cache for this producer
      this.queryClient.setQueryData(['producer', Number(data.id)], data)
      // Update all cached list queries beginning with ['producers', ...] so listings reflect the change immediately
      updateCachedListQueries(this.queryClient, ['producers'], data, { mode: 'insert', insertPosition: 'start' })

      // LISTING ['producers', sortProp, sortDir]: On CREATE → refetch listing queries so ordering/pagination is correct
      this.removeProducerListings()

      this.router.navigate(['/producer/', data['id']], { replaceUrl: true })
    },
  }))

  updateProducer = injectMutation(() => ({
    mutationFn: () => {
      if (this.producerForm.invalid) {
        throw new Error('Form is invalid')
      }
      const producerDataToSave = this.getDataToSave()
      return this.producerService.updateProducer(producerDataToSave, producerSchema)
    },
    onSuccess: async data => {
      this.submitted.set(false)
      // this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.producerForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.producerForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.producer.updated'))
      // Update detail cache for this producer
      this.queryClient.setQueryData(['producer', Number(data.id)], data)
      // Update all cached list queries beginning with ['producers', ...] so listings reflect the update immediately
      updateCachedListQueries(this.queryClient, ['producers'], data)

      // LISTING ['producers', sortProp, sortDir]: On UPDATE → if id is present in cached pages, refetch that exact query; otherwise do nothing
      this.removeProducerListings()

      this.router.navigate(['/producer', data['id']])
    },
  }))

  deleteProducer = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      const confirmation = confirm(
        this.translateService.instant('producer.delete.confirmation', { name: this.producerForm.value.name })
      )
      if (confirmation) {
        await this.producerService.deleteProducer(this.queryProducer.data())
      }
    },
    onSuccess: () => {
      this.submitted.set(false)
      this.notificationService.success(this.translateService.instant('alert.producer.deleted'))
      // Prune deleted producer from any cached lists (supports infinite queries and arrays)
      const idStr = String(this.route.snapshot.params['id'])
      updateCachedListQueries(this.queryClient, ['producers'], { id: Number(idStr) } as any, { mode: 'delete' })
      // Remove detail cache for this producer tood remove ???
      // this.queryClient.removeQueries({ queryKey: ['producer', Number(idStr)], exact: true })

      // LISTING ['producers', sortProp, sortDir]: On DELETE → if id is present in cached pages, remove it; otherwise do nothing
      this.removeFromListingsIfPresent(Number(idStr))

      this.router.navigate(['/producers'], { replaceUrl: true })
    },
  }))

  isCreating = computed(() => {
    return !this.route.snapshot.params['id']
  })
  loading = computed(() => {
    return this.queryProducer.isFetching() || this.queryProducer.isPending()
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private titleService: TitleService
  ) {
    // Forces this route to reload fresh (rather than being reused) when navigating between
    // different producer ids. This mutates the app-wide Router's routeReuseStrategy though, not
    // something scoped to this component - left in place it silently breaks route reuse (e.g.
    // wizard-style child routes elsewhere, like order creation) for the rest of the session. Put
    // the original back on the way out.
    this.previousShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute.bind(this.router.routeReuseStrategy)
    this.router.routeReuseStrategy.shouldReuseRoute = () => false
    // Populate the form whenever query data becomes available/changes (works for cache hits too)
    effect(() => {
      const d = this.queryProducer.data()
      const id = this.route.snapshot.params['id']
      if (id && d && this.producerForm.pristine) {
        this.fillFormValue(d)
      }
    })
  }

  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.previousShouldReuseRoute
  }

  public fillFormValue(producer) {
    const value = {
      name: producer.name ?? '',
    }

    this.producerForm.setValue(value)
    // Store initial values snapshot and reset form dirty state
    this.initialFormValue.set(structuredClone(value))
    this.producerForm.markAsPristine()
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.producerForm.getRawValue() as any
    const initial = this.initialFormValue() as any

    // Fallback: before snapshot is ready
    if (!initial) {
      return this.producerForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))
    return norm(current?.name) !== norm(initial?.name)
  }

  getDataToSave() {
    const current = this.producerForm.getRawValue() as any
    const patch: any = {}

    // Include id when editing
    const routeId = this.route.snapshot.params['id']
    if (routeId) patch.id = Number(routeId)

    // Always include required fields
    patch.name = current.name

    return patch
  }

  // ===== Helpers for cache handling of producer listings =====
  private isProducersListingKey(key: unknown[]): boolean {
    if (!Array.isArray(key)) return false
    if (key.length !== 3) return false
    return key[0] === 'producers' && typeof key[1] === 'string' && typeof key[2] === 'string'
  }

  private listingContainsId(cached: any, id: number | string): boolean {
    const idStr = String(id)
    if (!cached || !Array.isArray(cached.pages)) return false
    for (const page of cached.pages) {
      const items = Array.isArray(page) ? page : Array.isArray(page?.items) ? page.items : undefined
      if (!Array.isArray(items)) {
        continue
      }
      if (items.some((it: any) => String(it?.id) === idStr)) {
        return true
      }
    }
    return false
  }

  private removeIdFromCachedListing(key: unknown[], id: number | string): void {
    const cached: any = this.queryClient.getQueryData(key as any)
    if (!cached || !Array.isArray(cached.pages)) return
    const idStr = String(id)
    const newPages = cached.pages.map((page: any) => {
      if (Array.isArray(page)) {
        return page.filter((it: any) => String(it?.id) !== idStr)
      }
      if (Array.isArray(page?.items)) {
        return { ...page, items: page.items.filter((it: any) => String(it?.id) !== idStr) }
      }
      return page
    })
    this.queryClient.setQueryData(key as any, { ...cached, pages: newPages })
  }

  // CREATE → refetch all cached producer listings (['producers', sortProp, sortDir])
  private removeProducerListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['producers'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  // DELETE → if present in cached listings, remove it; otherwise do nothing
  private removeFromListingsIfPresent(id: number | string): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['producers'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      if (!this.isProducersListingKey(key as unknown[])) continue
      if (!this.listingContainsId(cached, id)) continue
      this.removeIdFromCachedListing(key as unknown[], id)
    }
  }

  goBack(): void {
    historyBack()
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.producerForm.controls
  }
}
